import { forwardRef } from 'react';
import ReactSelect, { Props as ReactSelectProps, StylesConfig, GroupBase } from 'react-select';
import { Controller, Control, FieldValues, Path, RegisterOptions } from 'react-hook-form';

export interface SelectOption {
  label: string;
  value: string | number;
}

interface SelectProps<T extends FieldValues> extends Omit<ReactSelectProps<SelectOption, boolean, GroupBase<SelectOption>>, 'name'> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  isSearchable?: boolean;
  rules?: RegisterOptions;
}

const customStyles: StylesConfig<SelectOption, boolean> = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? '#3b82f6' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
    '&:hover': {
      borderColor: '#3b82f6',
    },
    minHeight: '42px',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#3b82f6'
      : state.isFocused
      ? '#dbeafe'
      : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    cursor: 'pointer',
    fontSize: '0.875rem',
    '&:active': {
      backgroundColor: '#2563eb',
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '0.25rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af',
    fontSize: '0.875rem',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#1f2937',
    fontSize: '0.875rem',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#dbeafe',
    borderRadius: '0.25rem',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#1e40af',
    fontSize: '0.875rem',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#1e40af',
    '&:hover': {
      backgroundColor: '#bfdbfe',
      color: '#1e3a8a',
    },
  }),
};

function SelectComponent<T extends FieldValues>(
  { name, control, label, error, required, helperText, isSearchable = true, rules, ...rest }: SelectProps<T>,
  ref: any
) {
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field: { onChange, onBlur, value, ref } }) => {
          // Handle both single and multi-select values
          const selectedValue = rest.isMulti
            ? rest.options?.filter((option: any) =>
                Array.isArray(value) && value.includes(option.value)
              ) || []
            : rest.options?.find((option: any) => option.value === value) || null;

          return (
            <ReactSelect
              {...rest}
              ref={ref}
              value={selectedValue}
              onChange={(option: any) => {
                onChange(rest.isMulti ? option?.map((o: SelectOption) => o.value) : option?.value);
              }}
              onBlur={onBlur}
              styles={customStyles}
              classNamePrefix="react-select"
              className="react-select-container"
              isSearchable={isSearchable}
              closeMenuOnSelect={rest.closeMenuOnSelect ?? (rest.isMulti ? false : true)}
              menuPlacement="auto"
              maxMenuHeight={250}
              menuPortalTarget={document.body}
            />
          );
        }}
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      {helperText && !error && <p className="text-gray-500 text-xs mt-1">{helperText}</p>}
    </div>
  );
}

export const Select = forwardRef(SelectComponent) as <T extends FieldValues>(
  props: SelectProps<T> & { ref?: any }
) => ReturnType<typeof SelectComponent>;
